import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FeatureKey, MenuKey } from '../../enums';
import { AppSecurityService } from '../../services';

// One workspace widget (page/tile) the search can jump to. Mirrors the hub tiles + the
// dashboard/general pages. ⚠️ Maintenance: when a new widget/tile ships, add it here too
// (same rule as the area-home tile config).
interface IWorkspacePage {
    label: string;
    icon: string;
    route: string;
    workspace: string;
    /** Sidebar menu that must be granted (undefined = no menu gate, e.g. dashboard pages). */
    menu?: MenuKey;
    /** Feature the page is gated by (undefined = open to every signed-in user). */
    feature?: FeatureKey;
    /** Alternative: visible when ANY of these features is held. */
    anyFeature?: FeatureKey[];
}

interface ISearchGroup {
    workspace: string;
    pages: IWorkspacePage[];
}

/**
 * Top-bar workspace search: type to find any widget/page in the workspaces the user has access
 * to (per Casey: the popup lists the WIDGETS in each workspace - e.g. "Completion" -> the
 * Operations workspace's Completion % page). Pure client-side filter over the page registry,
 * gated by the same menu + feature grants that gate the real pages.
 */
@Component({
    selector: 'app-global-search',
    standalone: true,
    imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss']
})
export class GlobalSearchComponent {
    public term = '';
    public groups: ISearchGroup[] = [];
    public open = false;
    public mobileOpen = false;

    private static readonly pages: IWorkspacePage[] = [
        // Operations
        { label: 'Surveys', icon: 'app_registration', route: '/surveys', workspace: 'Operations', menu: MenuKey.Operations, feature: FeatureKey.Surveys },
        { label: 'Operations Portal', icon: 'hub', route: '/operations/portal', workspace: 'Operations', menu: MenuKey.Operations },
        { label: 'Completion %', icon: 'monitoring', route: '/operations/numbers-test', workspace: 'Operations', menu: MenuKey.Operations, feature: FeatureKey.OperationsNumbers },
        { label: 'Provisions', icon: 'rule', route: '/operations/provisions', workspace: 'Operations', menu: MenuKey.Operations, feature: FeatureKey.OperationsProvisions },
        // Billing
        { label: 'Time Sheets', icon: 'punch_clock', route: '/admin/timesheet', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.TimesheetAdmin },
        { label: 'Billing Exports', icon: 'folder_zip', route: '/admin/billing-exports', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.BillingExports },
        { label: 'Billing Rates', icon: 'request_quote', route: '/admin/rates', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.BillingRates },
        { label: 'View / Edit Rates', icon: 'table_view', route: '/admin/rates/explore', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.BillingRates },
        { label: 'Approvals', icon: 'fact_check', route: '/admin/approvals', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.Approvals },
        { label: 'Submit a Late Billing', icon: 'schedule_send', route: '/admin/late-billing', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.SubmitLateBilling },
        { label: 'Supplemental Paystubs', icon: 'receipt', route: '/admin/paysheets', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.Paysheets },
        { label: 'Billing Import', icon: 'upload_file', route: '/admin/billing-import', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.BillingImport },
        { label: 'Invoices', icon: 'request_page', route: '/admin/invoices', workspace: 'Billing', menu: MenuKey.Billing, feature: FeatureKey.Invoicing },
        // Sales
        { label: 'Leads', icon: 'person_search', route: '/leads', workspace: 'Sales', menu: MenuKey.Sales, feature: FeatureKey.Leads },
        // C Suite
        { label: 'Financial Information', icon: 'query_stats', route: '/executive/financial', workspace: 'C Suite', menu: MenuKey.CSuite, feature: FeatureKey.ExecutiveFinancials },
        { label: 'Financial Report (Quickbooks)', icon: 'account_balance', route: '/executive/quickbooks', workspace: 'C Suite', menu: MenuKey.CSuite, feature: FeatureKey.QuickbooksFinancials },
        { label: 'Quarterly Revenue', icon: 'calendar_view_month', route: '/executive/quarterly', workspace: 'C Suite', menu: MenuKey.CSuite, feature: FeatureKey.ExecutiveFinancials },
        { label: 'Expenses', icon: 'account_balance_wallet', route: '/executive/expenses', workspace: 'C Suite', menu: MenuKey.CSuite, feature: FeatureKey.CsuiteExpenses },
        { label: 'Revenue Modeler', icon: 'ssid_chart', route: '/executive/modeler', workspace: 'C Suite', menu: MenuKey.CSuite, feature: FeatureKey.ExecutiveFinancials },
        // Human Resources
        { label: 'Company Payroll', icon: 'payments', route: '/admin/payroll', workspace: 'Human Resources', menu: MenuKey.Hr, feature: FeatureKey.CompanyPayroll },
        { label: 'Employee Compensation', icon: 'attach_money', route: '/admin/compensation', workspace: 'Human Resources', menu: MenuKey.Hr, feature: FeatureKey.HrCompensation },
        { label: 'Payroll Requests', icon: 'pending_actions', route: '/admin/payroll-requests', workspace: 'Human Resources', menu: MenuKey.Hr, anyFeature: [FeatureKey.PayrollRequests, FeatureKey.PayrollRequestsApprove] },
        { label: 'Clock Records', icon: 'schedule', route: '/hr/clock-records', workspace: 'Human Resources', menu: MenuKey.Hr, feature: FeatureKey.ClockRecords },
        { label: 'Clock Compliance Report', icon: 'schedule', route: '/hr/clock-records/compliance', workspace: 'Human Resources', menu: MenuKey.Hr, feature: FeatureKey.ClockCompliance },
        { label: 'Clock Payroll Export', icon: 'schedule', route: '/hr/clock-records/export', workspace: 'Human Resources', menu: MenuKey.Hr, feature: FeatureKey.ClockPayrollExport },
        // IS&T
        { label: 'Onboarding', icon: 'how_to_reg', route: '/ist/onboarding', workspace: 'IS&T', menu: MenuKey.Ist, feature: FeatureKey.IstOnboarding },
        { label: 'Accounts', icon: 'alternate_email', route: '/ist/accounts', workspace: 'IS&T', menu: MenuKey.Ist, feature: FeatureKey.IstAccounts },
        { label: 'Password Resets', icon: 'lock_reset', route: '/ist/password-resets', workspace: 'IS&T', menu: MenuKey.Ist, feature: FeatureKey.IstPasswordReset },
        { label: 'Support Requests', icon: 'support_agent', route: '/ist/support', workspace: 'IS&T', menu: MenuKey.Ist, feature: FeatureKey.IstSupport },
        { label: 'Portal Documents', icon: 'library_books', route: '/ist/documents', workspace: 'IS&T', menu: MenuKey.Ist, feature: FeatureKey.PortalDocumentsManage },
        // Administrator
        { label: 'User Administration', icon: 'manage_accounts', route: '/admin/user', workspace: 'Administrator', menu: MenuKey.Administrator, feature: FeatureKey.UserAdmin },
        { label: 'Roles & Access', icon: 'badge', route: '/admin/roles', workspace: 'Administrator', menu: MenuKey.Administrator, feature: FeatureKey.RoleAdmin },
        { label: 'Teams', icon: 'groups', route: '/admin/teams', workspace: 'Administrator', menu: MenuKey.Administrator, feature: FeatureKey.TeamAdmin },
        { label: 'Supervisor Page', icon: 'supervisor_account', route: '/supervisor', workspace: 'Administrator', menu: MenuKey.Administrator, feature: FeatureKey.Supervisor },
        { label: 'Logins & User History', icon: 'login', route: '/admin/logins', workspace: 'Administrator', menu: MenuKey.Administrator, feature: FeatureKey.LoginsHistory },
        { label: 'OFSC Technician Matching', icon: 'link', route: '/admin/ofsc-resource-map', workspace: 'Administrator', menu: MenuKey.Administrator, feature: FeatureKey.OfscResourceMap },
        { label: 'Assistant Knowledge', icon: 'menu_book', route: '/admin/assistant-knowledge', workspace: 'Administrator', menu: MenuKey.Administrator, feature: FeatureKey.AiAssistantKnowledge },
        // General (dashboard pages - no menu gate). PTO is feature-gated only (like its sidebar
        // item) so employees without any workspace menu still find it.
        { label: 'Time Off Requests', icon: 'beach_access', route: '/pto', workspace: 'General', anyFeature: [FeatureKey.PtoRequests, FeatureKey.PtoManagerApprove] },
        { label: 'Attendance', icon: 'event_available', route: '/attendance', workspace: 'General', feature: FeatureKey.Attendance },
        { label: 'Dashboard', icon: 'dashboard', route: '/home', workspace: 'General' },
        { label: 'IS&T Support', icon: 'contact_support', route: '/support', workspace: 'General' },
        { label: 'Company Updates', icon: 'campaign', route: '/company-updates', workspace: 'General' },
        { label: 'Resource Links', icon: 'link', route: '/resource-links', workspace: 'General' },
        { label: 'Settings', icon: 'settings', route: '/settings', workspace: 'General' }
    ];

    constructor(private appSecurity: AppSecurityService, private router: Router, private host: ElementRef) { }

    public onInput(): void {
        const q = this.term.trim().toLowerCase();
        if (q.length < 1) {
            this.open = false;
            this.groups = [];
            return;
        }

        const matches = GlobalSearchComponent.pages.filter(p =>
            this.accessible(p) && (p.label.toLowerCase().includes(q) || p.workspace.toLowerCase().includes(q)));

        const byWorkspace = new Map<string, IWorkspacePage[]>();
        for (const p of matches) {
            const list = byWorkspace.get(p.workspace) ?? [];
            list.push(p);
            byWorkspace.set(p.workspace, list);
        }
        this.groups = Array.from(byWorkspace.entries()).map(([workspace, pages]) => ({ workspace, pages }));
        this.open = true;
    }

    public onFocus(): void {
        if (this.term.trim().length >= 1 && this.groups.length) {
            this.open = true;
        }
    }

    public pick(page: IWorkspacePage): void {
        this.open = false;
        this.mobileOpen = false;
        this.term = '';
        this.groups = [];
        this.router.navigateByUrl(page.route);
    }

    public toggleMobile(): void {
        this.mobileOpen = !this.mobileOpen;
        if (!this.mobileOpen) { this.open = false; }
    }

    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        if (!this.host.nativeElement.contains(event.target)) {
            this.open = false;
            this.mobileOpen = false;
        }
    }

    @HostListener('document:keydown.escape')
    public onEscape(): void {
        this.open = false;
        this.mobileOpen = false;
    }

    /** Same gating as the real page: the workspace menu (when it has one) + the page's feature. */
    private accessible(page: IWorkspacePage): boolean {
        if (page.menu && !this.appSecurity.hasMenu(page.menu)) { return false; }
        if (page.anyFeature) { return page.anyFeature.some(f => this.appSecurity.hasFeature(f)); }
        if (page.feature) { return this.appSecurity.hasFeature(page.feature); }
        return true;
    }
}
